import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { scrollToSection, WHATSAPP_URL } from "@/lib/scroll";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CLIPS = [
  { n: "OPEN", c: "#FF3B30", w: "14%" },
  { n: "B-ROLL", c: "#8E2DE2", w: "24%" },
  { n: "SFX", c: "#FF9500", w: "10%" },
  { n: "TITLES", c: "#00F2FE", w: "16%" },
  { n: "GRADE", c: "#FF3B30", w: "22%" },
  { n: "MIX", c: "#8E2DE2", w: "14%" },
];

export default function Hero() {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 110, damping: 18 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 110, damping: 18 });

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  const onMouseMove = (e) => {
    const r = sectionRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pb-20 pt-32"
    >
      {/* ambient gradient field */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-showreel-video"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        >
          <source src="/media/showreel-v3.mp4" type="video/mp4" />
          <source src="/media/showreel-v3.webm" type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-[#070709]/85 via-[#070709]/35 to-[#070709]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-transparent to-[#070709]/35" />
        <div className="orb absolute -left-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-[#FF3B30] opacity-[0.12] blur-[150px]" />
        <div className="orb-2 absolute right-[-8rem] top-1/4 h-[32rem] w-[32rem] rounded-full bg-[#8E2DE2] opacity-[0.14] blur-[160px]" />
        <div className="orb-3 absolute bottom-[-6rem] left-1/3 h-[26rem] w-[26rem] rounded-full bg-[#00F2FE] opacity-[0.06] blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />
      </div>

      <div className="wrap relative grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
        {/* left — type */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="hero-eyebrow"
            className="eyebrow mono-label text-[11px] text-[#FF3B30]"
          >
            Video Editor &amp; Motion Designer
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display font-black uppercase leading-[0.85] tracking-tight"
          >
            <span className="block overflow-hidden">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                animate="show"
                custom={0}
                className="block text-[clamp(4.8rem,13vw,11.5rem)]"
              >
                Ayan
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                animate="show"
                custom={1}
                className="text-gradient block text-[clamp(4.8rem,13vw,11.5rem)]"
              >
                Jafri
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg"
          >
            I cut videos that keep people watching —{" "}
            <span className="text-white">100+ projects</span> delivered for creators like{" "}
            <span className="text-white">Dhruv Rathee, Tech Burner, warikoo</span> &amp;{" "}
            <span className="text-white">Unspeakable</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp-button"
              className="btn-primary"
            >
              <MessageCircle className="h-5 w-5" />
              Book an edit
            </a>
            <button
              data-testid="hero-cta-work-button"
              onClick={() => scrollToSection("#work")}
              className="btn-ghost"
            >
              See the work
              <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </div>

        {/* right — portrait */}
        <div className="lg:col-span-5">
          <motion.div
            style={{ rotateX: rX, rotateY: rY, y: imgY, transformPerspective: 1000 }}
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm lg:ml-auto lg:mr-0"
            data-testid="hero-portrait"
          >
            <motion.div style={{ y: glowY }} aria-hidden="true" className="absolute -inset-10 -z-10">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FF3B30]/25 via-[#8E2DE2]/20 to-[#00F2FE]/15 blur-[90px]" />
            </motion.div>

            <div className="rounded-[28px] bg-gradient-to-br from-[#FF3B30] via-[#8E2DE2] to-[#00F2FE] p-[1.5px]">
              <div className="overflow-hidden rounded-[26px] bg-[#0E0E12]">
                <img
                  src="/media/ayan-portrait.png"
                  alt="Ayan Jafri, video editor"
                  className="aspect-[4/5] w-full object-cover"
                  loading="eager"
                />
              </div>
            </div>

            <div className="absolute -left-4 top-8 flex items-center gap-2 rounded-full border border-white/10 bg-black/60 px-4 py-2 backdrop-blur-xl">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[#FF3B30]" />
              <span className="mono-label text-[10px] text-white">REC</span>
            </div>
            <div className="absolute -right-3 bottom-10 rounded-full border border-white/10 bg-black/60 px-4 py-2 backdrop-blur-xl">
              <span className="mono-label text-[10px] text-white">4K · 24FPS</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* timeline strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="wrap relative mt-16"
        aria-hidden="true"
      >
        <div className="flex items-center gap-4 border-t border-white/5 pt-5">
          <span className="mono-label hidden text-[10px] text-zinc-500 sm:block">00:00:00:00</span>
          <div className="flex h-9 flex-1 items-center gap-1.5 overflow-hidden">
            {CLIPS.map((c) => (
              <div
                key={c.n}
                style={{ width: c.w, backgroundColor: `${c.c}22`, borderColor: `${c.c}55` }}
                className="flex h-full min-w-fit items-center justify-center rounded-md border px-3"
              >
                <span className="mono-label text-[9px]" style={{ color: c.c }}>
                  {c.n}
                </span>
              </div>
            ))}
          </div>
          <span className="mono-label hidden text-[10px] text-zinc-500 sm:block">00:01:12:07</span>
        </div>
      </motion.div>
    </section>
  );
}
